// Define the columns for the TableData component

import { Link } from "react-router-dom";

import { Column, ColumnDef } from "@tanstack/react-table";

import {
  ArrowSortIcon,
  CircleCheckIcon,
  LoaderIcon,
  MoreIcon,
  WarningIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { COUNTRY_NAME, DATASET_CONNECTOR_NAME } from "@/constants/dataset";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetExploreItem,
  EDatasetConnector,
  ETargetinImportStatus
} from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";
import { formatDate } from "@/utils/date";
import { formatStorageFromKB } from "@/utils/format";
import { formatNumber } from "@/utils/number";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import AggregationBadge from "./AggregationBadge";
import BadgeStatus from "./BadgeStatus";

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
>;

/**
 * Wires import state into the rows. Omitted for the plain list, where nothing is
 * in flight, leaving every cell as it was.
 */
export interface DatasetImportActions {
  getStatus: (datasetId: string) => ETargetinImportStatus | undefined;
  /** Stops this dataset's import only; asks for confirmation first. */
  onCancel: (datasetId: string) => void;
  /** Runs this dataset's import again. */
  onRetry: (datasetId: string) => void;
}

/**
 * Wires per-dataset sync into the rows. Omitted with the integration off, so no
 * row offers Sync.
 */
export interface DatasetSyncActions {
  /** Pulls this dataset's latest data from its connector. */
  onSync: (dataset: DatasetExploreItem) => void;
  /** True between the pull starting and its result landing. */
  isSyncing: (datasetId: string) => boolean;
}

/** Shared by every status icon, so they stay the same size and are read alike. */
const STATUS_ICON_PROPS = { width: 16, height: 16, role: "img" } as const;

/** Marks a row being imported, or just settled. */
function ImportStatusIndicator({
  status
}: Readonly<{ status: ETargetinImportStatus }>) {
  switch (status) {
    case ETargetinImportStatus.LOADING:
      return (
        <LoaderIcon
          {...STATUS_ICON_PROPS}
          aria-label="Importing dataset"
          data-testid="import-status-loading"
          className="animate-spin shrink-0 size-6"
        />
      );
    case ETargetinImportStatus.SUCCESS:
      return (
        <CircleCheckIcon
          {...STATUS_ICON_PROPS}
          aria-label="Import completed"
          data-testid="import-status-success"
          className="shrink-0 fill-shamrock-green size-6"
        />
      );
    case ETargetinImportStatus.FAILED:
      return (
        <WarningIcon
          {...STATUS_ICON_PROPS}
          aria-label="Import failed"
          data-testid="import-status-failed"
          className="shrink-0 fill-cinnabar size-6"
        />
      );
  }
}

/** The one action a row still being pulled offers. */
const importRowAction = (
  status: ETargetinImportStatus.LOADING | ETargetinImportStatus.FAILED,
  { onCancel, onRetry }: Pick<DatasetImportActions, "onCancel" | "onRetry">
) => {
  switch (status) {
    case ETargetinImportStatus.LOADING:
      return { label: "Cancel", testId: "row-action-cancel", run: onCancel };
    case ETargetinImportStatus.FAILED:
      return { label: "Retry", testId: "row-action-retry", run: onRetry };
  }
};

/** Shared shell so every row's actions open the same way. */
function RowActionsMenu({
  datasetId,
  children
}: Readonly<{ datasetId: string; children: React.ReactNode }>) {
  return (
    <div className="flex justify-center">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Dataset actions"
            data-testid={`row-actions-${datasetId}`}
          >
            <MoreIcon className="h-4 w-4 fill-txtBody" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-xl" side="left">
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Actions offered for a row still being imported. */
function ImportRowActions({
  datasetId,
  status,
  onCancel,
  onRetry
}: Readonly<{
  datasetId: string;
  status: ETargetinImportStatus.LOADING | ETargetinImportStatus.FAILED;
  onCancel: (datasetId: string) => void;
  onRetry: (datasetId: string) => void;
}>) {
  const action = importRowAction(status, { onCancel, onRetry });

  return (
    <RowActionsMenu datasetId={datasetId}>
      <button
        type="button"
        data-testid={`${action.testId}-${datasetId}`}
        className="text-xs text-left px-3 py-2 text-txtBody hover:text-teal-500 focus:outline-none"
        onClick={() => action.run(datasetId)}
      >
        {action.label}
      </button>
    </RowActionsMenu>
  );
}

function SortedHeader({
  column,
  title
}: Readonly<{
  column: Column<DatasetExploreItem>;
  title: string;
}>) {
  return (
    <Button
      variant="ghost"
      className="flex flex-row items-center h-0 p-0"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    >
      <p className="text-xs font-semibold text-txtBody">{title}</p>
      <ArrowSortIcon width={16} height={16} />
    </Button>
  );
}

export const columns = (
  timezone?: {
    code: string;
    name: string;
  },
  importActions?: DatasetImportActions,
  /**
   * Draws the "Source" and "Total Row" columns. Left out with the Targetin
   * integration off, restoring the page's earlier column set.
   */
  showTargetinColumns?: boolean,
  syncActions?: DatasetSyncActions
): ExtendedColumnDef<DatasetExploreItem>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <SortedHeader column={column} title="Dataset Title" />
    ),
    cell: ({ getValue, row }) => {
      const importStatus = importActions?.getStatus(row.original.id);
      const title = (
        <>
          <p className="text-xs font-semibold truncate text-txtBody">
            {getValue() as string}
          </p>
          <p className="text-xs text-txtBody line-clamp-2">
            {row.original.short_description}
          </p>
        </>
      );

      // Only a row carrying a status needs room for the icon beside it; the rest
      // keep the markup they had.
      if (!importStatus) return title;

      return (
        <div className="flex items-center gap-2">
          <div className="min-w-0">{title}</div>
          <span className="ml-auto">
            <ImportStatusIndicator status={importStatus} />
          </span>
        </div>
      );
    },
    enableSorting: true
  },
  // Both show a sort control, but the list API has no key for either: pressing
  // one falls back to the default order. getSortValues in the page is what keeps
  // sort_by valid — enableSorting does not, toggleSorting ignores it.
  ...(showTargetinColumns
    ? ([
        {
          accessorKey: "connector",
          header: ({ column }) => (
            <SortedHeader column={column} title="Source" />
          ),
          cell: (info) => (
            <p className="text-xs text-txtBody">
              {
                DATASET_CONNECTOR_NAME[
                  (info.getValue() as EDatasetConnector) ?? EDatasetConnector.LI
                ]
              }
            </p>
          ),
          enableSorting: true
        },
        {
          accessorKey: "total_rows",
          header: ({ column }) => (
            <SortedHeader column={column} title="Total Row" />
          ),
          cell: (info) => (
            <p className="text-xs text-txtBody">
              {formatNumber(info.getValue() as number)}
            </p>
          ),
          enableSorting: true
        }
      ] satisfies ExtendedColumnDef<DatasetExploreItem>[])
    : []),
  {
    accessorKey: "country_id",
    header: ({ column }) => <SortedHeader column={column} title="Country" />,
    enableSorting: true,
    cell: (info) => (
      <div>{COUNTRY_NAME[info.getValue() as CountryCode | "all"]}</div>
    )
  },
  {
    accessorKey: "spatial_aggregation_type",
    header: ({ column }) => (
      <SortedHeader column={column} title="Spatial Aggregation" />
    ),
    cell: (info) => (
      <AggregationBadge
        value={
          info.getValue() as Exclude<
            SpatialAggregationType,
            EOutputAnalysisType
          >
        }
      />
    ),
    enableSorting: true
  },
  {
    accessorKey: "published",
    header: ({ column }) => <SortedHeader column={column} title="Status" />,
    cell: (info) => <BadgeStatus value={info.getValue() as boolean} />,
    enableSorting: true
  },
  {
    accessorKey: "created_by",
    header: () => (
      <p className="text-xs font-semibold text-txtBody">Added by</p>
    ),
    cell: (info) => (
      <p
        className="text-xs text-txtBody truncate"
        title={info.getValue() as string}
      >
        {info.getValue() as string}
      </p>
    )
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortedHeader column={column} title="Added at" />,
    cell: (info) => (
      <p className="text-xs text-txtBody">
        {formatDate(
          info.getValue() as string,
          DataSchemaType.DATE_TIME,
          timezone
        )}
      </p>
    ),
    enableSorting: true
  },
  {
    accessorKey: "updated_by",
    header: () => (
      <p className="text-xs font-semibold text-txtBody">Modified by</p>
    ),
    cell: (info) => (
      <p
        className="text-xs text-txtBody truncate"
        title={info.getValue() as string}
      >
        {info.getValue() as string}
      </p>
    )
  },
  {
    accessorKey: "updated_at",
    header: ({ column }) => (
      <SortedHeader column={column} title="Modified at" />
    ),
    cell: (info) => (
      <p className="text-xs text-txtBody">
        {formatDate(
          info.getValue() as string,
          DataSchemaType.DATE_TIME,
          timezone
        )}
      </p>
    ),
    enableSorting: true
  },
  {
    accessorKey: "size_kb",
    header: ({ column }) => <SortedHeader column={column} title="Size" />,
    cell: (info) => (
      <p className="text-xs text-txtBody">
        {formatStorageFromKB(info.getValue() as number)}
      </p>
    ),
    enableSorting: true
  },
  {
    accessorKey: "id",
    header: () => <p className="text-xs font-semibold text-txtBody">Action</p>,
    cell: (data) => {
      const importStatus = importActions?.getStatus(data.row.original.id);

      // Cancel/Retry belong to an import in flight; a landed one gets the same
      // menu as any other dataset.
      if (importStatus && importStatus !== ETargetinImportStatus.SUCCESS) {
        return (
          <ImportRowActions
            datasetId={data.row.original.id}
            status={importStatus}
            onCancel={importActions!.onCancel}
            onRetry={importActions!.onRetry}
          />
        );
      }

      // A dataset that came from Targetin can be pulled again, including an
      // import row once it landed. Without connector_info there is no record to
      // ask the connector for, so no Sync either.
      if (
        syncActions &&
        data.row.original.connector === EDatasetConnector.TARGETIN &&
        data.row.original.connector_info
      ) {
        const isSyncing = syncActions.isSyncing(data.row.original.id);

        return (
          <RowActionsMenu datasetId={data.row.original.id}>
            <div className="flex flex-col">
              <Link
                to={`/dataset-management/${data.row.original.id}`}
                className="text-xs hover:bg-gainsboro px-3 py-2"
              >
                View Details
              </Link>
              <button
                type="button"
                data-testid={`row-action-sync-${data.row.original.id}`}
                className="text-xs text-left px-3 py-2 hover:bg-gainsboro disabled:text-gainsboro disabled:hover:text-gainsboro"
                disabled={isSyncing}
                onClick={() => syncActions.onSync(data.row.original)}
              >
                Sync
              </button>
            </div>
          </RowActionsMenu>
        );
      }

      return (
        <RowActionsMenu datasetId={data.row.original.id}>
          <Link
            to={`/dataset-management/${data.row.original.id}`}
            className="text-xs px-3 py-2"
            state={(data.row.index % 3) + 1}
          >
            View Dataset
          </Link>
        </RowActionsMenu>
      );
    }
  }
];
